import React from "react";
import {
    Button,
    Icon,
    Table,
    Tabs,
    Tag,
    message,
    Tooltip,
    Popconfirm
} from "antd";
import ReviewListLayout from "../components/ReviewListLayout";
import useRequest from "utils/useRequest";
import {
    downStudentExamPaperList,
    ExamPaperItem,
    ExamType,
    getExamInfo,
    getExamMyCourseList,
    getStudentExamPaperList,
    collectingStuPaper,
    getZipPaperList,
    downStuExamScore,
    UpdateExamStuScore
} from "service/exam";
import { useHistory } from "react-router-dom";
import LoadingWrapper from "../../../components/LoadingWrapper";
import { isTrue, trimTitle } from "../../../service/service";
import moment from "moment";
import style from "./style.module.scss";
import TaskModal from "./components/TaskModal";
import useModalHandler from "utils/useModalHandler";
import { DownloadBtn } from "./components/DownloadBtn";
import useModalHandlerV3 from "../../../utils/v3/useModalHandler";
import EditExamScopeModal from "./components/EditExamScopeModal";

const { TabPane } = Tabs;

interface PageExamReviewListProps {
    match: {
        params: {
            examId: string;
            paperId: string;
            tab: string;
        };
    };
}

export default function PageExamReviewList({
    match: {
        params: { examId }
    }
}: PageExamReviewListProps) {
    const history = useHistory();
    const [examInfo, { loading: loadingExam }] = useRequest(() =>
        getExamInfo(examId)
    );
    const [list, { loading }] = useRequest(() => getExamMyCourseList(examId));
    const defaultTab = window.location.hash.substr(1) || "0";

    function onTabChange(tab: string) {
        window.location.hash = tab;
    }

    function onReview(item: ExamPaperItem) {
        if (!examInfo) return;
        if (examInfo.type === ExamType.Exam)
            history.push(
                `/exam/review/detail/${examId}/${item.paperId}/${item.studentId}/${item.studentNo}/${item.ban}`
            );
        else
            history.push(
                `/exam/thesis/review/detail/${examId}/${item.paperId}/${item.studentId}`
            );
    }

    function onCollection(item: ExamPaperItem) {
        if (moment().isAfter(examInfo?.endTime)) {
            collectingStuPaper(examId, item.paperId, item.studentNo).then(res =>
                message.success("收卷请求成功！")
            );
        } else {
            message.error("考试未结束！");
        }
    }

    return (
        <LoadingWrapper
            loading={loading || loadingExam}
            isEmpty={!list || !examInfo}>
            {() => (
                <ReviewListLayout examInfo={examInfo!}>
                    <Tabs
                        type="card"
                        defaultActiveKey={defaultTab}
                        onChange={onTabChange}>
                        {list!.map((course, index) => (
                            <TabPane
                                tab={`${trimTitle(course.courseName, 8)}-${
                                    course.courseSeq
                                }`}
                                key={index.toString()}>
                                <ExamReviewItem
                                    onCollection={onCollection}
                                    courseNo={course.courseNo}
                                    courseSeq={course.courseSeq}
                                    examId={examId}
                                    type={examInfo!.type}
                                    onReview={onReview}
                                    examName={examInfo?.name || ""}
                                />
                            </TabPane>
                        ))}
                    </Tabs>
                </ReviewListLayout>
            )}
        </LoadingWrapper>
    );
}

function ExamReviewItem({
    courseNo,
    courseSeq,
    examId,
    type,
    onReview,
    onCollection,
    examName
}: {
    courseNo: string;
    courseSeq: string;
    examId: string;
    type: ExamType;
    onReview: (studentId: ExamPaperItem) => void;
    onCollection: (studentNo: ExamPaperItem) => void;
    examName: string;
}) {
    const [examPaperList, { loading }, refreshData] = useRequest(() =>
        getStudentExamPaperList({ courseNo, courseSeq, examId })
    );

    const editModal = useModalHandlerV3({
        component: EditExamScopeModal
    });

    function editSuccess() {
        editModal.close();
        refreshData();
    }
    function editClick(data: UpdateExamStuScore) {
        editModal.open({
            ...data,
            onSuccess: editSuccess
        });
    }

    const columns = [
        {
            title: "姓名",
            dataIndex: "name",
            width: 120,
            render: (val: string, item: ExamPaperItem) => {
                return (
                    <div className={style.name}>
                        <div>{val}</div>
                        {isTrue(item.ban) && (
                            <Tooltip title="该考生存在缺考、弃考或作弊行为、被老师强制“禁考”。">
                                <Icon type="user-add" />
                            </Tooltip>
                        )}
                        {isTrue(item.stuHaveExamFlag) && (
                            <Tooltip title="考试过程中存在异常行为，请点击“查看详情”查看。">
                                <Icon type="warning" />
                            </Tooltip>
                        )}
                    </div>
                );
            }
        },
        {
            title: "学号",
            dataIndex: "studentNo"
        },
        {
            title: "提交状态",
            dataIndex: "studnetSubmit",
            width: 105,
            filters: [
                { text: "未参考", value: "2" },
                { text: "已提交", value: "1" },
                { text: "未提交", value: "0" }
            ],
            filterMultiple: true,
            onFilter: (value: string, record: any) =>
                record.studnetSubmit === value,
            render: (value: string) =>
                value === "1" ? (
                    <Tag color="#87d068">已提交</Tag>
                ) : (
                    <Tag>未提交</Tag>
                )
        },
        {
            title: "批改状态",
            dataIndex: "checkFlag",
            width: 105,
            filters: [
                { text: "已批改", value: "1" },
                { text: "未批改", value: "0" }
            ],
            filterMultiple: true,
            onFilter: (value: string, record: any) =>
                record.checkFlag === value,
            render: (value: string) =>
                value === "1" ? (
                    <Tag color="#87d068">已批改</Tag>
                ) : (
                    <Tag>未批改</Tag>
                )
        },
        {
            title: "完成时间",
            dataIndex: "endTime",
            render: (value: string) =>
                value ? moment(value).format("YYYY-MM-DD HH:mm") : ""
        },
        {
            title: "客观题分数",
            dataIndex: "objectiveScore",
            visible: type === ExamType.Exam
        },
        {
            title: "分数",
            dataIndex: "score",
            render: (text: any, item: any) => {
                return (
                    <div className={style.box}>
                        <p>
                            <Tooltip title={item.notes}>
                                {item?.nScore || text}
                            </Tooltip>
                            {(item?.nScore || text) && (
                                <Icon
                                    type="edit"
                                    className={style.edit}
                                    onClick={() => {
                                        editClick({
                                            examid: item.examId,
                                            paperid: item.paperId,
                                            score: item?.nScore || text,
                                            studentid: item.studentId
                                        });
                                    }}
                                />
                            )}
                        </p>
                        {!!item?.nScore && item?.nScore >= 0 && (
                            <p className={style.tips}>原始分数 {text}</p>
                        )}
                    </div>
                );
            }
        },
        {
            title: "操作",
            dataIndex: "action",
            width: 100,
            render: (value: string, item: ExamPaperItem) => (
                <div>
                    {isTrue(item.studnetSubmit) && (
                        <Button type="primary" onClick={() => onReview(item)}>
                            批卷
                        </Button>
                    )}
                    {item.times === "0" && (
                        <Popconfirm
                            title={
                                <div>收卷延迟时间约 2 分钟，是否确认收卷？</div>
                            }
                            onConfirm={() => onCollection(item)}
                            okText="确定"
                            cancelText="取消">
                            <Button type="primary">收卷</Button>
                        </Popconfirm>
                    )}
                </div>
            )
        }
    ].filter(item => item.visible !== false);

    function onExport() {
        downStudentExamPaperList({ courseNo, courseSeq, examId, type });
    }

    function onExportScore() {
        downStuExamScore({ courseNo, courseSeq, examId, type });
    }

    return (
        <>
            <Table
                loading={loading}
                rowKey={"studentId"}
                columns={columns}
                dataSource={examPaperList || []}
                bordered
                pagination={false}
                footer={currentPageData => (
                    <TableFooter
                        data={currentPageData}
                        onExport={onExport}
                        onExportScore={onExportScore}
                        courseNo={courseNo}
                        courseSeq={courseSeq}
                        examId={examId}
                        examName={examName}
                        type={type}
                    />
                )}
            />

            {editModal.render()}
        </>
    );
}

/**
 * 表尾
 */
function TableFooter({
    data,
    onExport,
    onExportScore,
    courseNo,
    courseSeq,
    examId,
    examName,
    type
}: {
    data: ExamPaperItem[];
    onExport: () => void;
    onExportScore: () => void;
    courseNo: string;
    courseSeq: string;
    examId: string;
    examName: string;
    type: ExamType;
}) {
    const modal = useModalHandler();
    const [paperList, { loading }, updateData] = useRequest(
        () => getZipPaperList(examId, courseNo, courseSeq),
        {},
        [examId, courseNo, courseSeq]
    );

    //打包全部试卷
    const handleDownload = async () => {
        // try {
        //     await downloadAllExamPaper(examId, courseNo, courseSeq);
        //     await updateData();
        //     modal.open();
        // } catch (e) {
        //     ResultMessage.error(errorMessage(e));
        // }
    };

    return (
        <div>
            <div style={{ textAlign: "right" }}>
                <span>当前人数: {data.length}</span>
            </div>

            <div style={{ textAlign: "center" }}>
                <Button type="default" onClick={onExport}>
                    <Icon type="download" /> 导出
                </Button>
                {type === ExamType.Exam && (
                    <Button
                        type="default"
                        onClick={onExportScore}
                        className={style.exportScoreBtn}>
                        <Icon type="download" /> 导出(选择填空分数不汇总)
                    </Button>
                )}
                <DownloadBtn name={examName} data={data} />
            </div>

            <TaskModal
                visible={modal.visible}
                onCancel={modal.close}
                dataList={paperList || []}
                loading={loading}
                packageName={`${courseNo}-${courseSeq} ${examName}.zip`}
            />
        </div>
    );
}
